/**
 * 07 처방 등록 현황 · 07-1 상세 패널 API — Partner Prescription Registrations.
 * 기준 = 담당자 × 거래 병의원 × 처방 등록 월(+ 정산 월). 조회 전용.
 * 고를 수 있는 월은 /months 가 내려 주는 실제 (등록 월, 정산 월) 조합이다 — 프론트가 임의로 만들지 않는다.
 * 년월(yyyyMM)은 형식만 검증하고 범위 제한은 없다.
 */
import type { PartnerTradeStatus } from '@/entities/business-relationship/api/business-relationship.api';
import { createApiPath } from '@/shared/api/endpoints';
import { apiClient, toQueryParams } from '@/shared/api/http-client';
import type { PageParams, PageResponse } from '@/shared/api/types';

/** 확정 처방 엑셀은 요청 안에서 만들어 올린다 — 50,000행까지 기다린다 */
const EXPORT_TIMEOUT = 120_000;

/** 그 달 업체 범위 이미지가 1장 이상이면 REGISTERED */
export type RegistrationStatus = 'REGISTERED' | 'UNREGISTERED';

export const REGISTRATION_STATUS_LABEL: Record<RegistrationStatus, string> = {
  REGISTERED: '등록',
  UNREGISTERED: '미등록',
};

/**
 * 처방 등록 년월 선택지 — 실제 이미지에 저장된 (등록 월, 정산 월) 조합.
 * 같은 등록 월에 정산 월이 여럿일 수 있어 두 값을 함께 다룬다.
 */
export interface RegistrationPeriod {
  /** yyyyMM */
  registrationYearMonth: string;
  /** yyyyMM */
  settlementYearMonth: string;
}

export interface PrescriptionRegistrationListParams extends PageParams {
  /** yyyyMM. 생략하면 직전 월 */
  registrationYearMonth?: string;
  /** yyyyMM. 주면 두 년월이 모두 일치하는 이미지만 집계. 생략하면 등록 월 전체 */
  settlementYearMonth?: string;
  /** 병의원명·요양기관번호 부분 검색 */
  keyword?: string;
  /**
   * 담당자(다중, 최대 1000) — /partner/filters/users 의 userId. userIds=1&userIds=2 로 보낸다.
   * 없으면 전체.
   */
  userIds?: number[];
  /** 생략하면 전체 (등록 · 미등록) */
  registrationStatus?: RegistrationStatus;
  /** 확인 대기 이미지 1장 이상인 행만. 서버 기본 false */
  pendingOnly?: boolean;
  /** 없으면 서버 기본값 CLINIC_NAME */
  sort?: PrescriptionRegistrationSort;
}

/**
 * 정렬 기준 (RDL-113). 이름은 가나다 오름차순, 이름이 없는 행은 그 키에서 맨 뒤.
 * - CLINIC_NAME(기본, 병의원명순): 병의원명 → 담당자 업체명
 * - CHECK_NEEDED(확인 필요순): 미등록 먼저 → 확인 대기 많은 순 → 병의원명 → 담당자 업체명
 * - COMPANY_NAME(담당자순): 담당자 업체명 → 병의원명
 * - IMAGE_COUNT_DESC(등록 파일 많은순): imageCount 많은 순 → 병의원명 → 담당자 업체명
 * 정의되지 않은 값은 400.
 */
export const PRESCRIPTION_REGISTRATION_SORTS = [
  'CLINIC_NAME',
  'CHECK_NEEDED',
  'COMPANY_NAME',
  'IMAGE_COUNT_DESC',
] as const;

export type PrescriptionRegistrationSort =
  (typeof PRESCRIPTION_REGISTRATION_SORTS)[number];

/** 목록 1행 = 담당자 × 병의원. 정렬은 sort 로 고른다 */
export interface PrescriptionRegistration {
  userId: number;
  userCompanyName: string;
  userRepresentativeName: string;
  clinicId: number;
  clinicName: string | null;
  ykiho: string | null;
  region: string | null;
  /** 폐업일(yyyy-MM-dd). 폐업 병의원도 행은 노출한다 */
  clinicClosedOn: string | null;
  registrationStatus: RegistrationStatus;
  /** pendingImageCount + verifiedImageCount */
  imageCount: number;
  /** 확인 대기 = VERIFIED 외 전부(미확인·수정·미승인) */
  pendingImageCount: number;
  verifiedImageCount: number;
}

/** 요약 카드 — 목록 검색 조건과 무관한, 선택한 년월 조합의 업체 전체 값 */
export interface PrescriptionRegistrationSummary {
  registrationYearMonth: string;
  /** 정산 월을 지정하지 않았으면 null */
  settlementYearMonth: string | null;
  /** 담당자 × 병의원 행 수. 같은 병의원을 담당자 둘이 거래하면 2 */
  totalClinicCount: number;
  registeredClinicCount: number;
  unregisteredClinicCount: number;
  pendingImageCount: number;
  verifiedImageCount: number;
  /** 등록률(정수 %, 버림). 행이 없으면 0 */
  registrationRate: number;
}

export interface PrescriptionRegistrationDetailParams {
  userId: number;
  clinicId: number;
  /** yyyyMM. 생략하면 업체 범위 이미지가 있는 최근 월, 없으면 직전 월 */
  registrationYearMonth?: string;
}

/** 상세 패널 월 행 — 목록의 같은 월·같은 행 값과 같다 */
export interface RegistrationMonth {
  registrationYearMonth: string;
  /** 정산 월 = 처방 등록 월 + 1개월 */
  settlementYearMonth: string;
  registrationStatus: RegistrationStatus;
  imageCount: number;
  pendingImageCount: number;
  verifiedImageCount: number;
  /** 기준 월 행이면 true. 기준 월에 거래·이미지가 없으면 전부 false */
  selected: boolean;
}

/** 상세 패널 거래 행 — 거래처 정보와 같은 행 정의 */
export interface RegistrationTrade {
  relationPharmaCompanyId: number;
  drugCode: string | null;
  pharmaCompanyId: number;
  pharmaCompanyName: string | null;
  drugName: string | null;
  tradeStatus: PartnerTradeStatus;
  tradeStatusView: string;
  endedAt: string | null;
}

/**
 * 07-1 상세 패널. 404 PARTNER_TRADE_NOT_FOUND — 업체 범위 밖이거나
 * 오늘 거래처도 아니고 최근 12개월 처방 등록 현황 행도 없는 담당자·병의원.
 */
export interface PrescriptionRegistrationDetail {
  /** 실제 적용된 기준 월 */
  registrationYearMonth: string;
  userId: number;
  userCompanyName: string | null;
  userRepresentativeName: string | null;
  clinicId: number;
  clinicName: string | null;
  ykiho: string | null;
  region: string | null;
  clinicClosedOn: string | null;
  /** 기준 월부터 과거 6개월, 최신순. 거래·이미지 없는 달은 빠진다 */
  months: RegistrationMonth[];
  /** 오늘 귀속 거래. 과거 귀속으로만 연 패널이면 빈 배열 */
  trades: RegistrationTrade[];
}

/**
 * 처방 등록 이미지 ZIP · 확정 처방 엑셀 다운로드 조건 — 두 API 가 같은 본문을 받는다.
 * 같은 조건이면 엑셀의 '처방전 이미지명' 이 ZIP 안 파일 경로와 같다.
 */
export interface PrescriptionExportRequest {
  /** yyyyMM. 필수 */
  registrationYearMonth: string;
  /** yyyyMM. 주면 등록 월·정산 월이 모두 일치하는 이미지(엑셀은 처방 행도)만 */
  settlementYearMonth?: string;
  /** 병의원명·요양기관번호 부분 일치 */
  keyword?: string;
  /** 제약사(최대 1000) — /partner/filters/pharma-companies 의 값. 엑셀은 행도 그 제약사만 */
  pharmaCompanyIds?: number[];
}

/**
 * 압축 작업 상태.
 * FAILED 는 요청 후 60분 안에 시작 못 했거나 시작 후 30분 안에 안 끝난 작업도 포함,
 * EXPIRED 는 압축 시작 후 50분이 지나 파일이 지워진 작업이다.
 */
export type ImageArchiveJobStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'EXPIRED';

export interface ImageArchiveJob {
  jobId: string;
  status: ImageArchiveJobStatus;
  /** 예: 처방등록이미지_202609_정산202610.zip */
  fileName: string;
  /** 압축 대상 이미지 수 */
  targetCount: number;
  /** COMPLETED 일 때만 — 실제로 담긴 장수 */
  imageCount: number | null;
  /** COMPLETED 일 때만 — 원본을 못 받아 빠진 장수 */
  skippedCount: number | null;
  /** COMPLETED 일 때만. 조회할 때마다 새로 발급되고 5분 뒤 만료 */
  downloadUrl: string | null;
  /** yyyy-MM-dd HH:mm:ss */
  expiresAt: string | null;
  errorCode: string | null;
}

/** 아직 끝나지 않은 작업 — 상태를 계속 조회한다 */
export const isImageArchiveInProgress = (status: ImageArchiveJobStatus) =>
  status === 'PENDING' || status === 'RUNNING';

export interface FinalizedPrescriptionExcel {
  /** 예: 확정처방내역_202609_정산202610.xlsx */
  fileName: string;
  /** 5분 유효 */
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export const prescriptionRegistrationService = {
  /** GET /partner/prescription-registrations */
  list: async (params: PrescriptionRegistrationListParams = {}) => {
    const { data } = await apiClient.get<
      PageResponse<PrescriptionRegistration>
    >(createApiPath.prescriptionRegistrations.list(), {
      params: toQueryParams(params),
    });
    return data;
  },

  /** GET /partner/prescription-registrations/summary — 년월(등록·정산)만 받는다 */
  summary: async (period: Partial<RegistrationPeriod> = {}) => {
    const { data } = await apiClient.get<PrescriptionRegistrationSummary>(
      createApiPath.prescriptionRegistrations.summary(),
      { params: toQueryParams(period) },
    );
    return data;
  },

  /**
   * GET /partner/prescription-registrations/months — 년월 드롭다운 선택지.
   * registrationYear(yyyy) 해에 등록된 조합만. 생략하면 서버가 올해로 본다(프론트는 항상 보낸다).
   * 등록 월 내림차순 → 같은 등록 월이면 정산 월 내림차순. 없으면 [].
   */
  months: async (registrationYear: string) => {
    const { data } = await apiClient.get<RegistrationPeriod[]>(
      createApiPath.prescriptionRegistrations.months(),
      { params: { registrationYear } },
    );
    return data;
  },

  /** GET /partner/prescription-registrations/detail */
  detail: async (params: PrescriptionRegistrationDetailParams) => {
    const { data } = await apiClient.get<PrescriptionRegistrationDetail>(
      createApiPath.prescriptionRegistrations.detail(),
      { params: toQueryParams(params) },
    );
    return data;
  },

  /**
   * POST /partner/prescription-registrations/images/archive — 202, 압축 작업 생성.
   * 같은 조건으로 5분 안에 만든 진행 중 작업이 있으면 같은 jobId 를 돌려준다.
   */
  requestImageArchive: async (body: PrescriptionExportRequest) => {
    const { data } = await apiClient.post<ImageArchiveJob>(
      createApiPath.prescriptionRegistrations.imageArchive(),
      toQueryParams(body),
    );
    return data;
  },

  /** GET /partner/prescription-registrations/images/archive/{jobId} — 압축 작업 상태 */
  imageArchiveJob: async (jobId: string) => {
    const { data } = await apiClient.get<ImageArchiveJob>(
      createApiPath.prescriptionRegistrations.imageArchiveJob(jobId),
    );
    return data;
  },

  /** POST /partner/prescription-registrations/finalized/excel — 동기 생성, 바로 다운로드 URL */
  finalizedExcel: async (body: PrescriptionExportRequest) => {
    const { data } = await apiClient.post<FinalizedPrescriptionExcel>(
      createApiPath.prescriptionRegistrations.finalizedExcel(),
      toQueryParams(body),
      // 엑셀을 만들고 올리는 동안 기다린다 (기본 10초로는 부족할 수 있다)
      { timeout: EXPORT_TIMEOUT },
    );
    return data;
  },
};
